<template>
  <div class="settings">
    <div class="settings__inner">
      <h2 class="settings__title">{{ t('profile.settings.title1') }}</h2>
      <div class="settings__block">
        <div class="settings__block-inner">
          <div class="settings__box">
            <ui-input
                :type="'text'"
                :placeholder="t('fields.firstName')"
                v-model="firstName"
            />
            <ui-input
                :type="'text'"
                :placeholder="t('fields.lastName')"
                v-model="lastName"
            />
          </div>
          <ui-input
              :type="'email'"
              :placeholder="t('fields.email')"
              v-model="email"
          />
        </div>
        <ui-button
            class="settings__button"
            @click="updateInfo"
            :isLoading="isLoading1"
        >
          {{ t('buttons.save') }}
          <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
            <path d="M9.5625 6.125L17.4375 14L9.5625 21.875" stroke="#008ECC" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </ui-button>
      </div>
    </div>
    <div class="settings__inner">
      <h2 class="settings__title">{{ t('profile.settings.title2') }}</h2>
      <div class="settings__block">
        <div class="settings__block-inner">
          <div class="settings__box">
            <ui-input
                :type="'email'"
                :placeholder="t('fields.newPassword')"
                :rules="[isPasswordValid]"
                v-model="password"
            />
            <ui-input
                :type="'email'"
                :placeholder="t('fields.confirmNewPassword')"
                :rules="[compareStrings]"
                v-model="confirmPassword"
            />
          </div>
        </div>
        <ui-button
            class="settings__button"
            @click="updatePassword"
            :isLoading="isLoading2"
        >
          {{ t('buttons.change') }}
          <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
            <path d="M9.5625 6.125L17.4375 14L9.5625 21.875" stroke="#008ECC" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </ui-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useRouter} from "vue-router";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";
import {isPasswordValid} from "@/core/rules/textFieldRules.js";
import UiButton from "@/components/ui/ui-button.vue";

const {t} = useI18n()
const authStore = useAuthStore()
const router = useRouter()

const accessToken = computed(() => authStore.accessToken)
const user = computed(() => authStore.user)
const userUuid = computed(() => {
  return authStore.user ? authStore.user.uuid : ''
})
const userEmail = computed(() => {
  return authStore.user ? authStore.user.email : ''
})

const firstName = ref(user.value ? user.value.firstName : '')
const lastName = ref(user.value ? user.value.lastName : '')
const email = ref(user.value ? user.value.email : '')
const password = ref('')
const confirmPassword = ref('')

const isLoading1 = ref(false)
const isLoading2 = ref(false)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const logout = async () => {
  authStore.setUser({
    user: null,
    accessToken: null
  })

  localStorage.removeItem('nest-refresh')

  await router.push({
    name: 'home',
    params: {
      locale: localStorage.getItem('nest-user-locale') || 'en-gb'
    }
  })
}

const updateInfo = async () => {
  if (!user.value) return

  isLoading1.value = true

  const res = await Auth.updateUser(
      `uuid: "${userUuid.value}"` +
      (email.value ? `, email: "${email.value}"` : "") +
      (firstName.value ? `, firstName: "${firstName.value}"` : "") +
      (lastName.value ? `, lastName: "${lastName.value}"` : ""),
      accessToken.value
  )

  if (res.data.updateUser) {
    ElNotification({
      message: t("popup.updateData"),
      type: "success"
    });

    if (userEmail.value !== email.value) {
      await logout()

      ElNotification({
        message: t("popup.confirmEmail"),
        type: "success"
      });

      authStore.setUser({
        user: res.data.updateUser.user,
        accessToken: accessToken.value
      })
    }

    authStore.setUser({
      user: res.data.updateUser.user,
      accessToken: accessToken.value
    })
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading1.value = false
}

const updatePassword = async () => {
  if (!user.value) return

  isLoading2.value = true

  const res = await Auth.updateUser(
      `uuid: "${userUuid.value}"` +
      (password.value ? `, password: "${password.value}"` : "") +
      (confirmPassword.value ? `, confirmPassword: "${confirmPassword.value}"` : ""),
      accessToken.value
  )

  if (res.data.updateUser) {
    ElNotification({
      message: t("popup.updateData"),
      type: "success"
    });

    if (userEmail.value !== email.value) {
      await logout()

      ElNotification({
        message: t("popup.confirmEmail"),
        type: "success"
      });

      authStore.setUser({
        user: res.data.updateUser.user,
        accessToken: accessToken.value
      })
    }

    authStore.setUser({
      user: res.data.updateUser.user,
      access: accessToken.value
    })
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading2.value = false
}
</script>

<style lang="scss" scoped>
.settings {
  background-color: #FBFBFB;
  background-image: url(@images/profileBg.png);
  background-repeat: no-repeat;
  background-size: cover;

  &__inner {
    padding: 45px 50px;
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  &__title {
    color: #008ECC;
    font-size: 20px;
    font-weight: 700;
    line-height: 114%;
  }

  &__block {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 200px;

    &-inner {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 25px;
    }
  }

  &__button {
    width: fit-content;
    padding: 10px 20px;
  }

  &__box {
    display: flex;
    align-items: flex-start;
    gap: 25px;
  }
}

@media (max-width: 1300px) {
  .settings {
    &__block {
      gap: 100px;
    }
  }
}

@media (max-width: 1000px) {
  .settings {
    &__block {
      flex-direction: column;
      gap: 30px;
    }
  }
}

@media (max-width: 600px) {
  .settings {
    &__inner {
      padding: 25px 30px;
    }

    &__box {
      flex-direction: column;
    }
  }
}
</style>